import { defineStore } from "pinia"

import syncService from "@/services/syncService"
import { SyncDraft } from "@/types/sync.type"

type DraftsState = {
  drafts: SyncDraft[]
  query: Record<string, any>
  count: number
  fetching: boolean
  fetchRequestId: number
  creatingRequest: boolean
}

type GetDraftsParams = {
  uuid: string
  page?: number
  pageSize?: number
  query?: Record<string, any>
}

export type UpdateDraftPayload = Partial<{
  _approve: boolean
  product: string
  offer: string
  delivery: string
  price: number
  condition_key: string
  condition_value: number
  warranty: number
}>

export const useSyncDraftsStore = defineStore("syncDrafts", {
  state: (): DraftsState => ({
    drafts: [],
    query: {},
    count: 0,
    fetching: false,
    fetchRequestId: 0,
    creatingRequest: false
  }),

  actions: {
    async getDrafts({ uuid, page = 1, pageSize = 10, query = {} }: GetDraftsParams) {
      const requestId = ++this.fetchRequestId
      try {
        this.fetching = true
        const response = await syncService.getDrafts({ pageSize, page, uuid, query })

        if (requestId === this.fetchRequestId) {
          const { results: drafts = [], count = 0 } = response.data
          this.drafts = drafts
          this.count = count
        }

        return response.data
      } catch (e: any) {
        if (e.message === "canceled") return

        throw e?.response?.data || {}
      } finally {
        if (requestId === this.fetchRequestId) {
          this.fetching = false
        }
      }
    },

    async updateDraft({
      payload,
      synchronizationUuid,
      draftUuid
    }: {
      payload: UpdateDraftPayload
      synchronizationUuid: string
      draftUuid: string
    }) {
      try {
        const response = await syncService.updateDraft({
          synchronizationUuid,
          draftUuid,
          payload
        })

        // Dispatch meta update would go here if we had meta store

        // Update local state
        const draftPayload = response.data
        this.drafts = this.drafts.map((draft) => {
          if (draft.uuid !== draftUuid) {
            return draft
          } else {
            return draftPayload
          }
        })

        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      }
    },

    async updateDrafts({
      payload,
      synchronizationUuid
    }: {
      payload: any
      synchronizationUuid: string
    }) {
      try {
        const response = await syncService.updateDrafts({
          synchronizationUuid,
          payload
        })

        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      }
    }
  }
})
